import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { useOfficials, usePoliceStations, useSectionVisibility, type Official, type PoliceStation } from "@/hooks/use-supabase-data";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, Phone, MapPin, Shield, Building2, Users, Home } from "lucide-react";

export default function KnowYourOfficials() {
  const { officials: higherOfficials, loading: higherLoading } = useOfficials('higher_authority', { onlyActive: true });
  const { officials: subDistrictOfficials, loading: subDistrictLoading } = useOfficials('sub_district', { onlyActive: true });
  const { officials: villageOfficials, loading: villageLoading } = useOfficials('village', { onlyActive: true });
  const { policeStations, loading: policeLoading } = usePoliceStations({ onlyActive: true });
  const { visibility, loading: visibilityLoading } = useSectionVisibility();

  // Default to all hidden while loading to prevent flash
  const isSectionVisible = (key: 'higher_authority' | 'sub_district' | 'village' | 'police_stations') => {
    if (visibilityLoading) return false; // Hide while loading
    return visibility[key] !== false; // Show if not explicitly false
  };

  // Show loading state while visibility is loading
  if (visibilityLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-slate-600">Loading officials sections...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="text-center">
            <h1 className="font-poppins text-4xl md:text-5xl font-bold text-foreground mb-2">
              Know Your Officials
            </h1>
            <p className="font-inter text-muted-foreground text-lg">
              Contact information for district, sub-district, and village officials, and police stations
            </p>
          </div>
        </div>

              {/* Higher Authority Officials Section */}
              {isSectionVisible('higher_authority') && (
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 neo-icon-box">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-poppins text-2xl md:text-3xl font-bold text-foreground">
                Higher Authority Officials
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                District Collector, Superintendent of Police, and other senior officials
              </p>
            </div>
          </div>

          {higherLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <OfficialCardSkeleton key={i} />
              ))}
            </div>
          ) : higherOfficials.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-inter text-muted-foreground">No higher authority officials added yet.</p>
            </div>
          ) : (
            <OfficialsGrid officials={higherOfficials} />
          )}
        </section>
        )}

              {/* Village Officials Section */}
              {isSectionVisible('village') && (
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 neo-icon-box">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-poppins text-2xl md:text-3xl font-bold text-foreground">
                Village Officials
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                Village-level officials and local administration
              </p>
            </div>
          </div>

          {villageLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <OfficialCardSkeleton key={i} />
              ))}
            </div>
          ) : villageOfficials.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-inter text-muted-foreground">No village officials added yet.</p>
            </div>
          ) : (
            <OfficialsGrid officials={villageOfficials} />
          )}
        </section>
        )}

              {/* Sub-District Officials Section */}
              {isSectionVisible('sub_district') && (
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 neo-icon-box">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-poppins text-2xl md:text-3xl font-bold text-foreground">
                Sub-District Officials
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                SDM, Tehsildar, and other sub-district level officials
              </p>
            </div>
          </div>

          {subDistrictLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <OfficialCardSkeleton key={i} />
              ))}
            </div>
          ) : subDistrictOfficials.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-inter text-muted-foreground">No sub-district officials added yet.</p>
            </div>
          ) : (
            <OfficialsGrid officials={subDistrictOfficials} />
          )}
        </section>
        )}

              {/* Police Stations Section */}
              {isSectionVisible('police_stations') && (
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 neo-icon-box">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-poppins text-2xl md:text-3xl font-bold text-foreground">
                Know Your Police Station
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                Police station details, address, and contact information for officials
              </p>
            </div>
          </div>

          {policeLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <div key={i} className="p-8 rounded-2xl bg-card neo-card">
                  <Skeleton className="h-6 w-3/4 mb-4 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-full mb-2 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-full mb-4 rounded-xl neo-card-sm" />
                  <Skeleton className="h-32 w-full mb-4 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-full mb-2 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-full rounded-xl neo-card-sm" />
                </div>
              ))}
            </div>
          ) : policeStations.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-inter text-muted-foreground">No police stations added yet.</p>
            </div>
          ) : (
            <PoliceStationsGrid stations={policeStations} />
          )}
        </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function OfficialsGrid({ officials }: { officials: Official[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {officials.map((official) => (
        <OfficialCard key={official.id} official={official} />
      ))}
    </div>
  );
}

function PoliceStationsGrid({ stations }: { stations: PoliceStation[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {stations.map((station) => (
        <PoliceStationCard key={station.id} station={station} />
      ))}
    </div>
  );
}

/** Matches `CommunityCard` / `OfficialCard` footprint: `h-[180px]`, `w-2/5` + `w-3/5`. */
function OfficialCardSkeleton() {
  return (
    <div className="h-full px-2">
      <div className="rounded-2xl bg-card neo-card h-[180px] overflow-hidden flex flex-row">
        <Skeleton className="w-2/5 h-full rounded-none neo-card-sm" />
        <div className="w-3/5 h-full p-5 flex flex-col justify-center gap-2 min-w-0">
          <Skeleton className="h-6 w-4/5 rounded-xl neo-card-sm" />
          <Skeleton className="h-4 w-1/2 rounded-xl neo-card-sm" />
          <Skeleton className="h-7 w-full max-w-[140px] rounded-xl neo-card-sm" />
        </div>
      </div>
    </div>
  );
}

interface OfficialCardProps {
  official: Official;
}

function OfficialCard({ official }: OfficialCardProps) {
  const initials = official.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const hasContact = !!(official.phone || official.email || official.address);

  return (
    <div className="h-full px-2">
      <div
        className="rounded-2xl bg-card neo-card transition-all duration-300 h-[180px] overflow-hidden flex flex-row"
        data-testid={`card-official-${official.id}`}
      >
        <div className="w-2/5 h-full flex-shrink-0 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 relative">
          {official.photo ? (
            <img
              src={official.photo}
              alt={official.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-3">
                <span className="text-2xl font-bold text-foreground font-poppins">
                  {initials}
                </span>
              </div>
              <p className="text-xs text-muted-foreground font-inter">No photo</p>
            </div>
          )}
        </div>

        <div className="w-3/5 h-full p-5 flex flex-col justify-center flex-shrink-0">
          <div className="text-left">
            <h3 className="font-poppins text-xl font-bold text-foreground mb-1.5">
              {official.name}
            </h3>
            {official.designation?.trim() && (
              <div className="inline-flex w-fit max-w-full items-center px-3 py-1.5 rounded-xl bg-background/80 neo-card-sm mb-2.5">
                <span className="text-xs font-semibold text-primary font-inter line-clamp-2 leading-snug">
                  {official.designation}
                </span>
              </div>
            )}

            {hasContact && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">
                {official.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="font-inter truncate">{official.phone}</span>
                  </div>
                )}
                {official.email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="font-inter truncate">{official.email}</span>
                  </div>
                )}
                {official.address && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-inter line-clamp-2 leading-snug">{official.address}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PoliceStationCardProps {
  station: PoliceStation;
}

function PoliceStationCard({ station }: PoliceStationCardProps) {
  return (
    <div className="p-8 rounded-2xl bg-card neo-card transition-all duration-300">
      <div className="mb-4">
        <h3 className="font-poppins text-xl font-bold text-foreground mb-2">
          {station.name}
        </h3>
        {station.address && (
          <div className="flex items-start gap-2 text-sm text-foreground mb-3">
            <div className="p-2 rounded-xl bg-background neo-icon-box flex-shrink-0 mt-0.5">
              <MapPin className="w-4 h-4 text-primary" />
            </div>
            <span className="font-inter">{station.address}</span>
          </div>
        )}
        <div className="space-y-2 mb-4">
          {station.email && (
            <div className="flex items-center gap-2 text-sm text-foreground">
              <div className="p-2 rounded-xl bg-background neo-icon-box flex-shrink-0">
                <Mail className="w-4 h-4 text-primary" />
              </div>
              <span className="font-inter break-all">{station.email}</span>
            </div>
          )}
        </div>
      </div>

      {station.map_embed_url && (
        <div className="mb-4">
          <div className="w-full h-48 rounded-lg overflow-hidden border border-border">
            <iframe
              src={station.map_embed_url}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-full h-full"
              title={`${station.name} Location Map`}
            />
          </div>
        </div>
      )}

      <div className="border-t border-border/50 pt-4 space-y-3">
        {station.inspector_name && (
          <div>
            <h4 className="font-poppins text-sm font-semibold text-foreground mb-2">
              Inspector
            </h4>
            <div className="space-y-1">
              <p className="font-inter text-sm text-foreground">{station.inspector_name}</p>
              {station.inspector_email && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1.5 rounded-lg bg-background neo-icon-box flex-shrink-0">
                    <Mail className="w-3 h-3 text-primary" />
                  </div>
                  <span className="break-all">{station.inspector_email}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {station.sub_inspector_name && (
          <div>
            <h4 className="font-poppins text-sm font-semibold text-foreground mb-2">
              Sub-Inspector
            </h4>
            <div className="space-y-1">
              <p className="font-inter text-sm text-foreground">{station.sub_inspector_name}</p>
              {station.sub_inspector_email && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="p-1.5 rounded-lg bg-background neo-icon-box flex-shrink-0">
                    <Mail className="w-3 h-3 text-primary" />
                  </div>
                  <span className="break-all">{station.sub_inspector_email}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

